import React, { useEffect } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  Filler,
} from 'chart.js';
import 'react-toastify/dist/ReactToastify.css';
import 'leaflet/dist/leaflet.css';
import { ToastContainer } from 'react-toastify';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { store } from './redux/store';
import Layout from './Layout';
import Login from './pages/Login';
import Auth, { routePathPrefix } from './Auth';
import Register from './pages/Register/Register';
import TrackingDetails from './pages/TrackingDetails';
import TrackingOrderNumber from './pages/TrackingOrderNumber';
import TrackingPostalCode from './pages/TrackingPostalCode';
import Return from './pages/Return';
import TokenLogin from './pages/TokenLogin';
import { startSessionWatcher } from './services/session';
import { SpinnerToastContainer } from './utils/spinnerToaster';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  Filler,
);

/**
 * Every route that is reachable *without* a session. Single source of truth:
 * the routes below are rendered from this table, and `<Auth>` is handed the
 * paths derived from it. Adding a public page here therefore cannot leave
 * `Auth` behind, which is what used to make it redirect end customers off
 * `/return/:orderId` onto the merchant login as soon as the route order changed.
 */
const PUBLIC_ROUTES: { path: string; element: React.ReactElement }[] = [
  { path: '/login/', element: <Login /> },
  { path: '/register', element: <Register /> },
  { path: '/register/:registrationKey', element: <Register /> },
  { path: '/tracking', element: <TrackingOrderNumber /> },
  { path: '/tracking/auth/:orderNumber', element: <TrackingPostalCode /> },
  { path: '/tracking/details/:trackingKey', element: <TrackingDetails /> },
  { path: '/return/:orderId/', element: <Return /> },
  { path: '/login/token', element: <TokenLogin /> },
  { path: '/auth/login-token', element: <TokenLogin /> },
];

const PUBLIC_PATHS = Array.from(new Set(PUBLIC_ROUTES.map(route => routePathPrefix(route.path))));

function App() {
  // A token change this tab did not make (another tab logged out / logged in as
  // somebody else, or a 401 handler dropped an expired token) has to drop the
  // previous user's cache here too — above all the cached access rights.
  useEffect(() => startSessionWatcher(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Router>
          <Routes>
            {PUBLIC_ROUTES.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}

            <Route
              path="/*"
              element={
                <Auth publicPaths={PUBLIC_PATHS}>
                  <Layout />
                </Auth>
              }
            />
          </Routes>
        </Router>
        <ToastContainer autoClose={300000} position="bottom-right" toastClassName="typo-text-14" />
        <SpinnerToastContainer />
      </Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
